import React, { useId } from 'react';
import '../css/diamond-radar.css';

const SIZE = 240;
const CX = SIZE / 2;
const CY = SIZE / 2;
/** Vertex radius; visual tip is shorter because of rounding. */
const R = 108;
const RINGS = 4;
/** Gap from the rounded outer tip to the label center. */
const LABEL_GAP = 14;
/** Corner softness for rings and data blob. */
const CORNER = 48;
/**
 * Vertical shear: right tip above center, left tip below.
 * Applied as Δy = −SKEW · (x − CX).
 */
const SKEW = 0.16;
/**
 * Non-linear ring radii (fraction of R): bands near the center are much larger.
 * Level 1..4 → ~55% / 22% / 14% / 9% of the radius each.
 */
const RING_T = [0, 0.6, 0.74, 0.88, 1];

/** Wide floor for needle shapes (1 axis or 2 opposite); narrow for triangle/square. */
const MIN_DATA_R_WIDE = 28;
const MIN_DATA_R_NARROW = 6;

const pointAt = (angleDeg, radius) => {
  const rad = (angleDeg * Math.PI) / 180;
  return {
    x: CX + radius * Math.sin(rad),
    y: CY - radius * Math.cos(rad),
  };
};

const shapePoint = ({ x, y }) => ({
  x,
  y: y - SKEW * (x - CX),
});

const cornerCut = (ringRadius, corner) => {
  const edge = ringRadius * Math.SQRT2;
  return Math.min(corner, edge * 0.48);
};

const roundedAxisRadius = (ringRadius, corner) => {
  const c = cornerCut(ringRadius, corner);
  const curr = pointAt(0, ringRadius);
  const prev = pointAt(270, ringRadius);
  const next = pointAt(90, ringRadius);
  const len = Math.hypot(curr.x - prev.x, curr.y - prev.y) || 1;
  const start = {
    x: curr.x - ((curr.x - prev.x) / len) * c,
    y: curr.y - ((curr.y - prev.y) / len) * c,
  };
  const end = {
    x: curr.x + ((next.x - curr.x) / len) * c,
    y: curr.y + ((next.y - curr.y) / len) * c,
  };
  const midY = 0.25 * start.y + 0.5 * curr.y + 0.25 * end.y;
  return CY - midY;
};

const roundedPath = (points, corner = CORNER) => {
  const n = points.length;
  if (n < 3) return '';

  const parts = [];
  for (let i = 0; i < n; i += 1) {
    const prev = points[(i - 1 + n) % n];
    const curr = points[i];
    const next = points[(i + 1) % n];

    const v1x = curr.x - prev.x;
    const v1y = curr.y - prev.y;
    const v2x = next.x - curr.x;
    const v2y = next.y - curr.y;
    const len1 = Math.hypot(v1x, v1y) || 1;
    const len2 = Math.hypot(v2x, v2y) || 1;
    const r1 = Math.min(corner, len1 * 0.48);
    const r2 = Math.min(corner, len2 * 0.48);

    const start = {
      x: curr.x - (v1x / len1) * r1,
      y: curr.y - (v1y / len1) * r1,
    };
    const end = {
      x: curr.x + (v2x / len2) * r2,
      y: curr.y + (v2y / len2) * r2,
    };

    if (i === 0) parts.push(`M ${start.x} ${start.y}`);
    else parts.push(`L ${start.x} ${start.y}`);
    parts.push(`Q ${curr.x} ${curr.y} ${end.x} ${end.y}`);
  }
  parts.push('Z');
  return parts.join(' ');
};

const normalizeAxes = (axes) =>
  (axes || []).map((axis, i) => ({
    id: axis.id ?? String(i),
    angle: axis.angle ?? i * 90,
  }));

const valuesToArray = (axes, values) => {
  if (Array.isArray(values)) return axes.map((_, i) => values[i] || 0);
  return axes.map(({ id }) => (values && values[id]) || 0);
};

const floorRadiusForValues = (valueList) => {
  const active = valueList.map((v, i) => (v > 0 ? i : -1)).filter((i) => i >= 0);
  if (active.length <= 1) return MIN_DATA_R_WIDE;
  if (active.length === 2) {
    const [a, b] = active;
    return Math.abs(a - b) === 2 ? MIN_DATA_R_WIDE : MIN_DATA_R_NARROW;
  }
  return MIN_DATA_R_NARROW;
};

const tipVisualRadius = (angle, tipR, adjPrevR, adjNextR, corner) => {
  const curr = shapePoint(pointAt(angle, tipR));
  const prev = shapePoint(pointAt(angle - 90, adjPrevR));
  const next = shapePoint(pointAt(angle + 90, adjNextR));
  const v1x = curr.x - prev.x;
  const v1y = curr.y - prev.y;
  const v2x = next.x - curr.x;
  const v2y = next.y - curr.y;
  const len1 = Math.hypot(v1x, v1y) || 1;
  const len2 = Math.hypot(v2x, v2y) || 1;
  const r1 = Math.min(corner, len1 * 0.48);
  const r2 = Math.min(corner, len2 * 0.48);
  const start = { x: curr.x - (v1x / len1) * r1, y: curr.y - (v1y / len1) * r1 };
  const end = { x: curr.x + (v2x / len2) * r2, y: curr.y + (v2y / len2) * r2 };
  const mid = {
    x: 0.25 * start.x + 0.5 * curr.x + 0.25 * end.x,
    y: 0.25 * start.y + 0.5 * curr.y + 0.25 * end.y,
  };
  return Math.hypot(mid.x - CX, mid.y - CY);
};

const vertexForRingLevel = (level, angle, adjPrevR, adjNextR) => {
  const ringR = R * RING_T[level];
  const corner = CORNER * RING_T[level];
  const target = tipVisualRadius(angle, ringR, ringR, ringR, corner);
  let lo = target;
  let hi = ringR * 1.25;
  for (let i = 0; i < 16; i += 1) {
    const mid = (lo + hi) / 2;
    const visual = tipVisualRadius(angle, mid, adjPrevR, adjNextR, corner);
    if (visual < target) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
};

const radiiForCap = (axes, valueList, cap, floorR) => {
  const caps = valueList.map((v) => Math.min(v, cap));
  let radii = caps.map((c) => (c > 0 ? R * RING_T[c] : floorR));
  radii = radii.map((r, i) => {
    const c = caps[i];
    if (c <= 0) return floorR;
    const prev = radii[(i - 1 + axes.length) % axes.length];
    const next = radii[(i + 1) % axes.length];
    return vertexForRingLevel(c, axes[i].angle, prev, next);
  });
  return radii;
};

const axisPoints = (axes, radii) =>
  axes.map(({ angle }, i) => shapePoint(pointAt(angle, radii[i])));

const resolveFill = (colors, fill) => {
  if (fill) return fill;
  const list = colors && colors.length ? colors : ['#ccc', '#ccc', '#ccc', '#ccc'];
  const n = list.length;
  const step = 360 / n;
  const stops = list.map((color, i) => `${color} ${i * step}deg`);
  stops.push(`${list[0]} 360deg`);
  return `conic-gradient(from 0deg at 50% 50%, ${stops.join(', ')})`;
};

/**
 * Universal diamond radar with optional height-map terraces.
 *
 * @param {Object} props
 * @param {Array<{id: string, angle?: number}>} props.axes — clockwise from top (default 0/90/180/270)
 * @param {Record<string, number>|number[]} props.values — activation count per axis (0…rings)
 * @param {string[]} [props.colors] — one color per axis for the base conic fill
 * @param {string} [props.fill] — CSS background override (skips conic from colors)
 * @param {Array<{id: string, content: React.ReactNode, title?: React.ReactNode}>} [props.labels]
 * @param {string} [props.className]
 * @param {string} [props.ariaLabel]
 * @param {boolean} [props.showHeightStrokes=true] — white terrace outlines above the base
 * @param {function} [props.renderLabel] — (axis, count) => ReactNode; wraps label content (e.g. Tooltip)
 */
export function DiamondRadarChart({
  axes: axesProp,
  values: valuesProp,
  colors,
  fill,
  labels,
  className,
  ariaLabel,
  showHeightStrokes = true,
  renderLabel,
}) {
  const uid = useId().replace(/:/g, '');
  const clipId = `diamond-radar-clip-${uid}`;

  const axes = normalizeAxes(axesProp);
  if (!axes.length) return null;

  const valueList = valuesToArray(axes, valuesProp);
  const maxCount = Math.max(0, ...valueList);
  if (maxCount <= 0 && !valueList.some(Boolean)) {
    // Still render empty grid + labels when all zeros.
  }

  const floorR = floorRadiusForValues(valueList);
  const heightLayers = [];
  for (let cap = Math.max(maxCount, 1); cap >= 1; cap -= 1) {
    if (maxCount > 0 && !valueList.some((v) => v >= 1)) break;
    if (maxCount === 0) break;
    const radii = radiiForCap(axes, valueList, cap, floorR);
    const corner = CORNER * RING_T[Math.min(cap, RINGS)];
    heightLayers.push({
      cap,
      path: roundedPath(axisPoints(axes, radii), corner),
      clipId: `${clipId}-h${cap}`,
    });
  }

  const axisEndR = roundedAxisRadius(R, CORNER);
  const labelR = axisEndR + LABEL_GAP;
  const fillBg = resolveFill(colors, fill);
  const labelById = Object.fromEntries((labels || []).map((one) => [one.id, one]));

  return (
    <div className={['diamond-radar', className].filter(Boolean).join(' ')}>
      <svg
        className="diamond-radar-svg"
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        role="img"
        aria-label={ariaLabel || axes.map(({ id }, i) => `${id}: ${valueList[i]}`).join(', ')}
      >
        <defs>
          {heightLayers.filter(({ cap }) => cap === maxCount).map(({ clipId: id, path }) => (
            <clipPath key={id} id={id}>
              <path d={path} />
            </clipPath>
          ))}
        </defs>

        {Array.from({ length: RINGS }, (_, i) => {
          const level = i + 1;
          const radius = R * RING_T[level];
          const pts = axisPoints(axes, axes.map(() => radius));
          return (
            <path
              key={`ring-${level}`}
              className="diamond-radar-ring"
              d={roundedPath(pts, CORNER * RING_T[level])}
            />
          );
        })}

        {heightLayers.map(({ cap, path, clipId: id }) => (
          <g key={`height-${cap}`}>
            {cap === maxCount ? (
              <g className="diamond-radar-fill" clipPath={`url(#${id})`}>
                <foreignObject x="0" y="0" width={SIZE} height={SIZE}>
                  <div
                    xmlns="http://www.w3.org/1999/xhtml"
                    className="diamond-radar-conic"
                    style={{ background: fillBg }}
                  />
                </foreignObject>
              </g>
            ) : (
              showHeightStrokes && <path className="diamond-radar-height-stroke" d={path} />
            )}
          </g>
        ))}
      </svg>

      {axes.map(({ angle, id }, i) => {
        const { x, y } = shapePoint(pointAt(angle, labelR));
        const count = valueList[i] || 0;
        const meta = labelById[id] || {};
        const content = meta.content ?? null;
        if (content == null && !renderLabel) return null;
        const inner = renderLabel
          ? renderLabel({ id, angle, count, ...meta })
          : <span className="diamond-radar-label-emoji">{content}</span>;
        return (
          <div
            key={`label-${id}`}
            className={`diamond-radar-label diamond-radar-label--${id}`}
            style={{ left: `${(x / SIZE) * 100}%`, top: `${(y / SIZE) * 100}%` }}
          >
            {inner}
          </div>
        );
      })}
    </div>
  );
}

export const DIAMOND_RADAR = {
  SIZE,
  R,
  RINGS,
  RING_T,
  CORNER,
  SKEW,
};
